import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { DatePicker } from "@mui/x-date-pickers-pro";

import { useAppSelector } from "../../app/hooks";
import {
  selectTrayEntries,
  clearTray,
  removeFromTray
} from "./bulkEndTraySlice";
import { applyBulkEnd, toEndDateIso, BulkEndTarget, BulkEndMembership } from "./bulkEndExecution";
import { fetchUser } from "./usersAPI";
import { useAllPrograms } from "../Programs/programUtils";
import { ProgramMembership, ProgramRole } from "../../API";
import { ToastStyle } from "../../app/types";
import {
  setToastMessage,
  setToastStyle,
  setShowToast,
  setToastAutoHideDuration
} from "../../common-ui/toastSlice";

type Props = {
  open: boolean;
  onClose: () => void;
  // Called after a run so the caller can refetch the user list.
  onDone: () => void;
};

// "end" = end active memberships; "changeDate" = change the date on already-ended
// memberships. Both set end_date via the same endpoint (SE-1724's edit-ended
// path handles the latter) and both apply the involvement floor server-side.
type Mode = "end" | "changeDate";

type FreshMemberships = { active: BulkEndMembership[]; ended: BulkEndMembership[] };

const key = (userId: string, membershipId: string) =>
  `${userId}:${membershipId}`;

const labelFor = (e: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  userId: string;
}) =>
  [e.firstName, e.lastName].filter(Boolean).join(" ") || e.email || e.userId;

export const BulkEndReviewDialog = ({ open, onClose, onDone }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const entries = useAppSelector(selectTrayEntries);
  const { programs } = useAllPrograms();

  const [mode, setMode] = useState<Mode>("end");
  const [endDatePick, setEndDatePick] = useState<Date | null>(new Date());
  const [endDateError, setEndDateError] = useState<string | null>(null);
  // membership keys the admin has UNCHECKED (default: everything is included).
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [running, setRunning] = useState(false);
  // Fresh memberships per user, fetched when the dialog opens — the tray only
  // snapshots at add-time, so re-fetch so the review reflects CURRENT state.
  const [fresh, setFresh] = useState<Record<string, FreshMemberships>>({});
  const [loadingMemberships, setLoadingMemberships] = useState(false);

  useEffect(() => {
    if (!open) return;
    // The dialog stays mounted between opens (MUI), so start every open from a
    // clean slate: default the end date to TODAY, clear any stale validation
    // error, and re-include every membership (drop prior unchecks).
    setEndDatePick(new Date());
    setEndDateError(null);
    setExcluded(new Set());
    let cancelled = false;
    setLoadingMemberships(true);
    (async () => {
      const result: Record<string, FreshMemberships> = {};
      const ids = entries.map((e) => e.userId);
      const mapMs = (m: ProgramMembership): BulkEndMembership => ({
        id: m.id,
        programId: m.programId ?? "",
        programRole: m.programRole,
        programName: programs.find((p) => p.id === m.programId)?.name ?? null
      });
      for (let i = 0; i < ids.length; i += 10) {
        const slice = ids.slice(i, i + 10);
        await Promise.all(
          slice.map(async (uid) => {
            try {
              // /api/user (same source as EditUser) — its memberships carry real
              // endDate values, unlike the programsByUser path.
              const u = await fetchUser(uid);
              const items = (u?.programMemberships?.items ??
                []) as (ProgramMembership | null)[];
              result[uid] = {
                active: items
                  .filter((m): m is ProgramMembership => !!m && !m.endDate)
                  .map(mapMs),
                ended: items
                  .filter((m): m is ProgramMembership => !!m && !!m.endDate)
                  .map(mapMs)
              };
            } catch {
              const snap = entries.find((e) => e.userId === uid);
              result[uid] = {
                active: (snap?.memberships ?? []).map((m) => ({
                  id: m.id,
                  programId: m.programId ?? "",
                  programRole: (m.programRole ?? "Attending") as ProgramRole,
                  programName: m.programName
                })),
                ended: []
              };
            }
          })
        );
      }
      if (!cancelled) {
        setFresh(result);
        setLoadingMemberships(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-fetch only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleMembership = (userId: string, membershipId: string) => {
    const k = key(userId, membershipId);
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const membershipsFor = (userId: string): BulkEndMembership[] => {
    const f = fresh[userId];
    if (!f) return [];
    return mode === "end" ? f.active : f.ended;
  };

  const targets: BulkEndTarget[] = useMemo(
    () =>
      entries
        .map((e) => ({
          userId: e.userId,
          userLabel: labelFor(e),
          memberships: membershipsFor(e.userId).filter(
            (m) => !excluded.has(key(e.userId, m.id))
          )
        }))
        .filter((tgt) => tgt.memberships.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, excluded, fresh, mode]
  );

  const totalMemberships = targets.reduce(
    (n, tgt) => n + tgt.memberships.length,
    0
  );

  const confirmDisabled =
    !endDatePick ||
    !!endDateError ||
    totalMemberships === 0 ||
    running ||
    loadingMemberships;

  const run = async () => {
    if (!endDatePick || endDateError) return;
    setRunning(true);
    try {
      const outcome = await applyBulkEnd(targets, toEndDateIso(endDatePick));
      const ok = outcome.succeeded.length;
      const floor = outcome.floorRejected.length;
      const scopedRefused = outcome.scopedRefused.length;
      const failed = outcome.failed.length;
      const verb = mode === "end" ? "ended" : "updated";
      const parts = [`${ok} ${verb}`];
      if (floor) parts.push(`${floor} couldn't be set that early`);
      if (scopedRefused)
        parts.push(
          `${scopedRefused} skipped — end Coach / Rotation Director in Edit User`
        );
      if (failed) parts.push(`${failed} failed`);
      const style: ToastStyle = failed
        ? "error"
        : floor || scopedRefused
          ? "warning"
          : "success";
      dispatch(setToastStyle(style));
      dispatch(setToastMessage(parts.join(" · ")));
      dispatch(setToastAutoHideDuration(null));
      dispatch(setShowToast(true));
      dispatch(clearTray());
      onDone();
      onClose();
    } finally {
      setRunning(false);
    }
  };

  const isEnd = mode === "end";

  return (
    <Dialog
      open={open}
      onClose={running ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="bulk-end-title"
    >
      <DialogTitle id="bulk-end-title">
        {isEnd
          ? t("End program memberships")
          : t("Change membership end dates")}
      </DialogTitle>
      <DialogContent dividers>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, v: Mode | null) => {
            if (v) {
              setMode(v);
              setExcluded(new Set());
            }
          }}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="end">{t("End active")}</ToggleButton>
          <ToggleButton value="changeDate">{t("Change ended date")}</ToggleButton>
        </ToggleButtonGroup>

        <DialogContentText sx={{ mb: 2 }}>
          {isEnd
            ? t(
                "Choose one end date for everyone below (today or earlier), then confirm which memberships to end. Memberships that can't be ended that early (the user had assessment activity after it) are reported back and left unchanged."
              )
            : t(
                "Choose one new end date for everyone below (today or earlier) to re-date their already-ended memberships. Dates that can't be set that early (the user had assessment activity after it) are reported back and left unchanged."
              )}
        </DialogContentText>
        <Box mb={2}>
          <DatePicker
            label={isEnd ? t("End date") : t("New end date")}
            value={endDatePick}
            maxDate={new Date()}
            onChange={(d) => setEndDatePick(d)}
            onError={(reason) => setEndDateError(reason)}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Box>

        {loadingMemberships ? (
          <Box display="flex" alignItems="center" gap={1} py={2}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t("Loading current memberships…")}
            </Typography>
          </Box>
        ) : (
          <Stack
            spacing={1}
            divider={
              <Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />
            }
          >
            {entries.map((e) => {
              const f = fresh[e.userId];
              const active = f?.active ?? [];
              const ended = f?.ended ?? [];
              // Show the user's FULL membership picture in both tabs, but only
              // the memberships that apply to THIS tab are selectable; the rest
              // render greyed-out + tagged. Keeps the two tabs symmetric — no
              // membership silently vanishes when you switch tabs.
              const applicable = isEnd ? active : ended;
              const applicableIds = new Set(applicable.map((m) => m.id));
              const rows = [...applicable, ...(isEnd ? ended : active)];
              const naTag = isEnd ? t("ended") : t("active");
              return (
                <Box key={e.userId}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="subtitle2">{labelFor(e)}</Typography>
                    <Tooltip title={t("Remove from list")}>
                      <IconButton
                        size="small"
                        aria-label={t("Remove from list")}
                        onClick={() => dispatch(removeFromTray(e.userId))}
                      >
                        <CloseRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {rows.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      {t("No memberships")}
                    </Typography>
                  ) : (
                    <FormGroup>
                      {rows.map((m) => {
                        const selectable = applicableIds.has(m.id);
                        const label = [m.programName, m.programRole]
                          .filter(Boolean)
                          .join(" · ");
                        return (
                          <FormControlLabel
                            key={m.id}
                            disabled={!selectable}
                            control={
                              <Checkbox
                                size="small"
                                disabled={!selectable}
                                checked={
                                  selectable && !excluded.has(key(e.userId, m.id))
                                }
                                onChange={
                                  selectable
                                    ? () => toggleMembership(e.userId, m.id)
                                    : undefined
                                }
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                color={
                                  selectable ? "text.primary" : "text.disabled"
                                }
                              >
                                {label}
                                {!selectable && (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.disabled"
                                  >
                                    {`  (${naTag})`}
                                  </Typography>
                                )}
                              </Typography>
                            }
                          />
                        );
                      })}
                    </FormGroup>
                  )}
                </Box>
              );
            })}
            {entries.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t("Your end list is empty.")}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button
          color="inherit"
          disabled={running || entries.length === 0}
          onClick={() => dispatch(clearTray())}
        >
          {t("Clear list")}
        </Button>
        <Box>
          <Button onClick={onClose} disabled={running}>
            {t("Cancel")}
          </Button>
          <LoadingButton
            variant="contained"
            loading={running}
            disabled={confirmDisabled}
            onClick={run}
            sx={{ ml: 1 }}
          >
            {isEnd
              ? t(
                  `End ${totalMemberships} membership${
                    totalMemberships === 1 ? "" : "s"
                  }`
                )
              : t(
                  `Change ${totalMemberships} end date${
                    totalMemberships === 1 ? "" : "s"
                  }`
                )}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
